import React from 'react'

import {
  Box,
  Display,
  Text,
  Heading1,
  Space,
  FontFamily,
  AlignItems,
  JustifyContent,
  Visible,
} from '@gousto-internal/citrus-react'
import TextInput from 'Form/Input'
import { useDispatch, useSelector } from 'react-redux'

import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import { useIsOptimizelyFeatureEnabled } from 'containers/OptimizelyRollouts'
import { StickyCTA } from 'routes/Signup/Components/StickyCTA/StickyCTA'
import {
  signupChangePostcode,
  signupChangeTempPostcode,
  signupGetCountByPostcode,
} from 'routes/Signup/signupActions'
import { signupConfig } from 'routes/Signup/signupConfig'
import {
  getDeliveryDaysError,
  getPostcodePending,
  getTempPostcode,
} from 'routes/Signup/signupSelectors'

import { Button } from '../../Button'
import { Image } from '../../Image'
import { PostcodeStepMessage } from './PostcodeStepMessage'

export function PostcodeStep({ nextStepName = '' }: { nextStepName: string | number }) {
  const isWizardFloatingCTAEnabled = useIsOptimizelyFeatureEnabled(
    'beetroots_wizard_floating_cta_enabled_v1',
  )

  const dispatch = useDispatch()

  const tempPostcode = useSelector(getTempPostcode)
  const deliveryDaysError = useSelector(getDeliveryDaysError)
  const postcodePending = useSelector(getPostcodePending)

  const handleClick = () => {
    dispatch(signupGetCountByPostcode(tempPostcode))
    dispatch(signupChangePostcode(tempPostcode, nextStepName))
  }
  const inputChangeHandler = (postcode: string) => dispatch(signupChangeTempPostcode(postcode))

  const renderResponsiveCta = () => (
    <>
      <Visible display={[Display.Block, Display.None]}>
        <StickyCTA
          disabled={tempPostcode.length < 5}
          dataTestId="postcodeStepStickyCTA"
          onClick={handleClick}
        >
          Continue
        </StickyCTA>
      </Visible>
      <Space size={[0, 2]} direction="horizontal" />
      <Box maxWidth={['0', '8.25rem']} flexGrow={1}>
        <Visible display={[Display.None, Display.Block]}>
          <Button
            disabled={tempPostcode.length < 5}
            data-testing="signupPostcodeCTA"
            onClick={handleClick}
            pending={postcodePending}
            width="full"
          >
            Continue
          </Button>
        </Visible>
      </Box>
    </>
  )

  return (
    <Box data-testing="signupPostcodeStep">
      <Box>
        <>
          <Heading1>{signupConfig.postCodeStep.title}</Heading1>
          <Image name="where-to-deliver" />
          <Space size={3} direction="vertical" />
        </>
      </Box>

      <Box>
        <form
          onSubmit={(e) => {
            if (e) {
              e.preventDefault()
            }
            handleClick()
          }}
          action="#"
        >
          <Box
            display={Display.Flex}
            alignItems={AlignItems.FlexEnd}
            justifyContent={JustifyContent.Center}
          >
            <Box
              flexBasis={isWizardFloatingCTAEnabled ? 'auto' : ['50%', 'auto']}
              width={isWizardFloatingCTAEnabled ? '100%' : [null, '100%']}
              maxWidth={isWizardFloatingCTAEnabled ? ['17.5rem', '13.5rem'] : [null, '13.5rem']}
            >
              <Text size={2} fontFamily={FontFamily.Bold}>
                Postcode
              </Text>
              <Space size={2} direction="vertical" />
              <TextInput
                isFixed
                placeholder="e.g. W3 7UP"
                onChange={inputChangeHandler}
                minLength={5}
                maxLength={8}
                value={tempPostcode}
                mask
                dataTesting="signupPostcodeInput"
                isInCheckout
                error={deliveryDaysError}
                autoComplete="postal-code"
              />
            </Box>
            {isWizardFloatingCTAEnabled ? (
              renderResponsiveCta()
            ) : (
              <>
                <Space size={2} direction="horizontal" />
                <Box maxWidth="8.25rem" flexGrow={1}>
                  <Button
                    disabled={tempPostcode.length < 5}
                    data-testing="signupPostcodeCTA"
                    onClick={handleClick}
                    pending={postcodePending}
                    width="full"
                  >
                    Continue
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </form>
      </Box>
      <Space size={[4, 6]} direction="vertical" />
      <PostcodeStepMessage deliveryDaysError={deliveryDaysError} />
      <RibbonTriggerContainer name="wizard-postcode-step" />
    </Box>
  )
}
